/**
 * The help-request reply desk (admin.posture.vision/help). pv-auth #433:
 *   GET  /provider/admin/help-requests?status=new|answered|all
 *   PUT  /provider/admin/help-requests/:id/staged-reply { subject?, body }   save a draft, never sends
 *   POST /provider/admin/help-requests/:id/send { subject?, body? }          the one Send button
 *   POST /provider/admin/help-requests/:id/close { reason }                  RESOLVED, nothing sent
 *
 * A row is one press of the in-app help button. The staged reply on it was
 * written by us or by Brian and is his to edit until it goes; Send mails it
 * through the product's own mailer (Reply-To Brian) and marks the row
 * answered. Nothing on this page sends by itself.
 */
export type HelpStatus = 'NEW' | 'ACKNOWLEDGED' | 'ANSWERED' | 'RESOLVED';

export interface HelpRow {
  id: string;
  userId: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  plan: string | null;
  platform: string;
  appVersion: string | null;
  locale: string | null;
  concern: string;
  status: HelpStatus;
  createdAt: string;
  notifiedAt: string | null;
  notifyError: string | null;
  account: { id: string; email: string; firstName: string | null; lastName: string | null } | null;
  /** Where the reply goes; null means nowhere to send (no address typed, no account). */
  replyTo: string | null;
  staged: { subject: string | null; body: string; by: string | null; at: string } | null;
  answered: { at: string; by: string | null; subject: string | null; body: string | null } | null;
  answerError: string | null;
}

export type HelpFilter = 'new' | 'answered' | 'all';

export interface HelpList {
  generatedAt: string;
  status: HelpFilter;
  counts: { new: number; answered: number; all: number };
  replyFrom: string;
  bookingUrl: string | null;
  suggestATime: string;
  rows: HelpRow[];
}

export type HelpSendResult =
  | { ok: true; row: HelpRow; sentTo: string }
  | { ok: false; code: 'not_found' | 'nothing_to_send' | 'no_address' | 'already_answered' | 'send_failed'; message: string; row: HelpRow | null };

export const PLATFORM_LABEL: Record<string, string> = {
  ios: 'iPhone app',
  android: 'Android app',
  desktop: 'Desktop app',
  web: 'Browser',
  unknown: 'Unknown device',
};

/** "iPhone app 1.4.1", "Browser", never a bare version. */
export const whereFrom = (row: Pick<HelpRow, 'platform' | 'appVersion'>): string => {
  const label = PLATFORM_LABEL[row.platform] ?? row.platform;
  return row.appVersion ? `${label} ${row.appVersion}` : label;
};

/** The name on the form, else the account's first name, else the address, else "no name". */
export const whoWrote = (row: Pick<HelpRow, 'name' | 'account' | 'email' | 'replyTo'>): string =>
  (row.name ?? '').trim() || (row.account?.firstName ?? '').trim() || row.replyTo || row.email || 'no name';

export const isOpen = (row: Pick<HelpRow, 'status'>): boolean => row.status === 'NEW' || row.status === 'ACKNOWLEDGED';

/** Hours waited, from the request to the answer (or to now when unanswered), whole hours. */
export const hoursWaited = (row: Pick<HelpRow, 'createdAt' | 'answered'>, now: Date = new Date()): number => {
  const end = row.answered ? new Date(row.answered.at).getTime() : now.getTime();
  return Math.max(0, Math.round((end - new Date(row.createdAt).getTime()) / 3_600_000));
};

/** Days waited, said plainly: "waited 25 days", "answered after 3 days", "today". */
export const waitedLine = (row: Pick<HelpRow, 'createdAt' | 'answered'>, now: Date = new Date()): string => {
  const days = Math.floor(hoursWaited(row, now) / 24);
  const span = days === 0 ? 'under a day' : days === 1 ? '1 day' : `${days} days`;
  return row.answered ? `answered after ${span}` : `waiting ${span}`;
};

/** Append the "Suggest a time" line once, after a blank line, never twice. */
export const withSuggestedTime = (body: string, line: string): string => {
  if (body.includes(line)) return body;
  const base = body.replace(/\s+$/, '');
  return base ? `${base}\n\n${line}` : line;
};

/** The composer's starting text: the staged reply, else empty. */
export const draftFor = (row: Pick<HelpRow, 'staged'>): { subject: string; body: string } => ({
  subject: row.staged?.subject ?? '',
  body: row.staged?.body ?? '',
});

export async function fetchHelpRequests(status: HelpFilter = 'new'): Promise<HelpList> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet(`/provider/admin/help-requests?status=${status}`, true)) as HelpList;
}

export async function stageHelpReply(id: string, input: { subject: string; body: string }): Promise<HelpRow> {
  const { apiPut } = await import('@/services/base');
  return (await apiPut(`/provider/admin/help-requests/${encodeURIComponent(id)}/staged-reply`, input, true)) as HelpRow;
}

/**
 * The one Send. The server answers 201 with ok true, or 409/502 with ok false
 * and the reason; the base client throws on a non-2xx, so the reason is read
 * off the thrown error's body when it carries one.
 */
export async function sendHelpReply(id: string, input: { subject: string; body: string }): Promise<HelpSendResult> {
  const { apiPost } = await import('@/services/base');
  try {
    return (await apiPost(`/provider/admin/help-requests/${encodeURIComponent(id)}/send`, input, true)) as HelpSendResult;
  } catch (err) {
    const e = err as Error & { data?: Partial<HelpSendResult>; response?: { data?: Partial<HelpSendResult> } };
    const data = e.data ?? e.response?.data;
    if (data && data.ok === false && typeof data.message === 'string') return data as HelpSendResult;
    throw err;
  }
}

/** Close without emailing anyone: a duplicate, the form's own error text, a test row. The reason is logged. */
export async function closeHelpRequest(id: string, reason: string): Promise<HelpRow> {
  const { apiPost } = await import('@/services/base');
  return (await apiPost(`/provider/admin/help-requests/${encodeURIComponent(id)}/close`, { reason }, true)) as HelpRow;
}
